// Client-side analytics tracker. Fire-and-forget, consent-gated, best-effort.
//
// Every event carries a stable `session_id` (and `visitor_id`) plus device,
// browser and acquisition context, so the funnel can be followed end-to-end
// and broken down by device / channel instead of being a pile of unconnected
// rows.
import { hasConsent } from "@/lib/cookie-consent";
import { logAnalyticsEvent } from "@/lib/analytics.functions";
import { getAttribution } from "@/lib/attribution";
import {
  getDeviceContext,
  getSessionId,
  getVisitorId,
  isReturningVisitor,
} from "@/lib/session-id";
import { readStoredAuthSession } from "@/lib/auth-session";

function platform(): string | null {
  if (typeof window === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mobi/i.test(ua)) return "mobile-web";
  return "web";
}

/** Current signed-in user id, when a session is present. */
function currentUserId(): string | null {
  try {
    return readStoredAuthSession()?.user?.id ?? null;
  } catch {
    return null;
  }
}

function isSignedIn(): boolean {
  try {
    return !!readStoredAuthSession();
  } catch {
    return false;
  }
}

export function track(
  eventName: string,
  properties: Record<string, unknown> = {},
  opts: { source?: string } = {},
) {
  emit(eventName, properties, opts, false);
}

/**
 * Operational (essential) event — NOT gated on analytics cookie consent.
 *
 * Reserved for the paid-signup journey: we must always be able to tell
 * "the person chose not to pay" apart from "our system failed to get them
 * to the payment page". These rows are first-party service diagnostics,
 * carry no marketing/UTM attribution, and are never used for advertising.
 */
export function trackOperational(
  eventName: string,
  properties: Record<string, unknown> = {},
  opts: { source?: string } = {},
) {
  emit(eventName, properties, opts, true);
}

function emit(
  eventName: string,
  properties: Record<string, unknown>,
  opts: { source?: string },
  essential: boolean,
) {
  if (typeof window === "undefined") return;
  if (!essential && !hasConsent("analytics")) return;



  // Read the returning flag BEFORE minting the visitor id, otherwise every
  // first-time visitor would look like a returning one.
  const returning = isReturningVisitor();
  const device = getDeviceContext();
  // Essential/operational events carry no marketing attribution.
  const attr = essential ? null : getAttribution();

  const payload = {
    event_name: eventName,
    session_id: getSessionId(),
    path: window.location.pathname + window.location.search,
    source: opts.source ?? null,
    platform: platform(),
    utm_source: attr?.utm_source ?? null,
    utm_medium: attr?.utm_medium ?? null,
    utm_campaign: attr?.utm_campaign ?? null,
    properties: {
      ...properties,
      visitor_id: getVisitorId(),
      returning_visitor: returning,
      signed_in: isSignedIn(),
      user_id: currentUserId(),
      referrer: attr?.referrer ?? null,
      acquisition_platform: attr?.platform ?? null,
      ...(device ?? {}),
      ...(essential ? { essential: true } : {}),
    },
  };

  // Fire-and-forget — never block UI on analytics.
  try {
    void logAnalyticsEvent({ data: payload }).catch(() => {});
  } catch {
    /* noop */
  }
}
